/* Pure UI helpers for Survey Responses (Figma CrewKnit spec).
   Display math only — no API calls, no hooks, no JSX. */

/** Response rate % (Figma: responses / sent). */
export function responseRate(completed: number, total: number): number {
  if (!total || total <= 0) return 0;
  return Math.round((completed / total) * 100);
}

/** Rate bar/text color token (Figma thresholds dc.html:8551). */
export function rateColorToken(rate: number): string {
  if (rate >= 80) return "var(--color-success-dark)";
  if (rate >= 65) return "var(--color-warning-dark)";
  return "var(--color-error-dark)";
}

/** Deterministic avatar gradient token from a name (initials come from real names). */
const AVATAR_GRADIENTS = [
  "bg-avatar-1",
  "bg-avatar-2",
  "bg-avatar-3",
  "bg-avatar-4",
  "bg-avatar-5",
  "bg-avatar-6",
] as const;

export function avatarGradientToken(name: string): string {
  let hash = 0;
  const s = name || "?";
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) % 997;
  }
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
}

/** Deterministic solid tile pair for survey rows (decorative, Figma-style). */
const SURVEY_TILES = [
  { tileBg: "bg-[var(--color-primary-100)]", tileColor: "text-[var(--color-primary-700)]" },
  { tileBg: "bg-[var(--color-success-light)]", tileColor: "text-[var(--color-success-dark)]" },
  { tileBg: "bg-[var(--color-info-light)]", tileColor: "text-[var(--color-info)]" },
  { tileBg: "bg-[var(--color-warning-light)]", tileColor: "text-[var(--color-warning-dark)]" },
  { tileBg: "bg-[var(--color-error-light)]", tileColor: "text-[var(--color-error-dark)]" },
  { tileBg: "bg-[var(--color-secondary-100)]", tileColor: "text-[var(--color-secondary-700)]" },
] as const;

export function surveyTileToken(key: string): { tileBg: string; tileColor: string } {
  let hash = 0;
  const s = key || "?";
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) % 997;
  }
  return SURVEY_TILES[hash % SURVEY_TILES.length];
}

/** "N answered" label for question blocks. */
export function answeredLabel(n: number): string {
  return `${n.toLocaleString()} answered`;
}

/** "Most selected: X (Y%)" callout line (Figma dc.html:8581). */
export function mostSelectedLine(topLabel: string, topN: number, answered: number): string {
  if (!topLabel || answered <= 0) return "";
  return `Most selected: ${topLabel} (${Math.round((topN / answered) * 100)}%)`;
}

/** Short audience summary for headers/nav state ("A, B +3 more" / "N assignees"). */
export function assignedSummary(names: string[], totalCount: number): string {
  const clean = (names || []).map((n) => (n || "").trim()).filter(Boolean);
  if (clean.length === 0) return totalCount > 0 ? `${totalCount} assignees` : "—";
  const shown = clean.slice(0, 2).join(", ");
  const remaining = Math.max(totalCount, clean.length) - Math.min(2, clean.length);
  return remaining > 0 ? `${shown} +${remaining} more` : shown;
}
